import React from 'react';
import block from 'bem-cn';
import { shallowEqual, useSelector } from 'react-redux';

import Spinner from 'components/Spinner';
import SVG from 'components/SVG';

import formatNumber from 'shared/helpers/formatNumber';
import dotSVG from 'shared/img/promotions/SuperadminBonusSistem/dot.svg';
import arrowUpSVG from 'shared/img/promotions/SuperadminBonusSistem/arrow-up.svg';
import arrowUpBigSVG from 'shared/img/promotions/SuperadminBonusSistem/arrow-up-big.svg';
import giftSVG from 'shared/img/promotions/SuperadminBonusSistem/gift.svg';

import { languages } from 'services/locale';

import useSuperadminBonusSistem from '../../model';
import { PICS_BY_LANG } from '../../model/data';

import './SuperadminBonusSistem.scss';

const b = block('superadmin-bonus-sistem');

const SuperadminBonusSistem = () => {
  const { lang, locale } = useSelector(
    state => ({
      lang: state.locale.lang,
      locale: state.locale.locale.superadminBonusSistem,
    }),
    shallowEqual,
  );

  const pics = PICS_BY_LANG[lang] || PICS_BY_LANG[languages.EN];

  const {
    isVisible,
    isLoading,
    isLoaded,
    currentGrade,
    nextGrade,
    isMaxGrade,
    amountDepositsLast30Days,
    progress,
    progressPercent,
    paymentWidget,
  } = useSuperadminBonusSistem();

  if (!isVisible) return null;

  if (!isLoaded) {
    return (
      <div className={b({ loading: isLoading })}>
        <Spinner isLoading={isLoading} />
      </div>
    );
  }

  const progressValue = Math.round(progressPercent);
  const currentGradeName = locale.grades?.[currentGrade?.id] || currentGrade.name;
  const nextGradeName = locale.grades?.[nextGrade?.id] || nextGrade?.name;

  const currentDepositBonusPercent = Number(currentGrade.depositBonusPercent).toFixed(2);
  const nextDepositBonusPercent = Number(nextGrade?.depositBonusPercent || 0).toFixed(2);
  const transitionBonusUsd = formatNumber(paymentWidget.transitionBonusUsd, 0);


  return (
    <div className={b({ grade: currentGrade.id, loading: isLoading })}>
      <Spinner isLoading={isLoading} />
      <div className={b('img-1')}>
        <img src={pics.first} alt="" />
      </div>
      <div className={b('card')}>
        <div className={b('label')}>
          <SVG className={b('label-dot')} svgProps={{ svg: dotSVG }} />
          <span>{locale.currentStatus}</span>
        </div>

        <h1 className={b('title')}>{currentGradeName}</h1>

        <div className={b('bonus-pill')}>
          <div className={b('bonus-pill-img')}>
            <SVG svgProps={{ svg: arrowUpSVG }} />
          </div>
          <span>
            +{currentDepositBonusPercent}% {locale.onEveryDeposit}
          </span>
        </div>

        <div className={b('divider')} />

        <div className={b('section-label')}>{locale.amountDepositsLast30Days}</div>
        <div className={b('amount')}>
          {formatNumber(amountDepositsLast30Days, 0)}
          <span>USDT</span>
        </div>


        {!isMaxGrade && (
          <>
            <div className={b('divider')} />

            <div className={b('progress-head')}>
              <div>
                <div className={b('section-label')}>{locale.progressToNextLevel}</div>
                <div className={b('progress-amount')}>
                  {formatNumber(progress.currentValueUsdt, 0)}
                  <span>/ {formatNumber(progress.targetValueUsdt, 0)} USDT</span>
                </div>
              </div>
              <div className={b('progress-percent')}>{progressValue}%</div>
            </div>

            <div className={b('progress-line')}>
              <div className={b('progress-value')} style={{ width: `${progressPercent}%` }} />
            </div>
          </>
        )}
      </div>

      {!isMaxGrade && (
        <div className={b('card', { next: true, grade: nextGrade.id })}>
          <div className={b('label')}>
            <span>{locale.nextLevel}</span>
          </div>

          <h2 className={b('next-title')}>{nextGradeName}</h2>

          <div className={b('divider')} />

          <div className={b('next-bonuses')}>
            <div className={b('next-bonus')}>
              <div className={b('next-bonus-icon')}>
                <SVG svgProps={{ svg: arrowUpBigSVG }} />
              </div>
              <div>
                <div className={b('next-bonus-label')}>{locale.reward}</div>
                <div className={b('next-bonus-value')}>+{nextDepositBonusPercent}%</div>
                <div className={b('next-bonus-text')}>{locale.perDeposit}</div>
              </div>
            </div>

            <div className={b('next-bonuses-separator')} />

            <div className={b('next-bonus')}>
              <div className={b('next-bonus-icon')}>
                <SVG svgProps={{ svg: giftSVG }} />
              </div>
              <div>
                <div className={b('next-bonus-label')}>{locale.levelUpBonus}</div>
                <div className={b('next-bonus-value')}>+{transitionBonusUsd}
                  <span>USDT</span>
                </div>
                <div className={b('next-bonus-text')}>{locale.oneTimeReward}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={b('img-2')}>
        <img src={pics.second} alt="" />
      </div>

      <div className={b('img-3')}>
        <img src={pics.third} alt="" />
      </div>
    </div>
  );
};

export default SuperadminBonusSistem;